import { createAction } from 'redux-api-middleware'
import qs from 'query-string'
import { HUBBLE_API_TOKEN } from '../store/middleware/APIUrl'
import { getCustomMessagesFromLayerExtra, setCustomMessagesIntoLayerExtra } from '../reducers/customMessages'
import { getSelectedStoreDevices } from '../reducers/stores'
import { updateLayerExtra } from './device'

const Types = {
    CUSTOM_MESSAGES_CREATE_REQUEST: 'CUSTOM_MESSAGES_CREATE##REQUEST',
    CUSTOM_MESSAGES_CREATE_SUCCESS: 'CUSTOM_MESSAGES_CREATE##SUCCESS',
    CUSTOM_MESSAGES_CREATE_FAILURE: 'CUSTOM_MESSAGES_CREATE##FAILURE',
    CUSTOM_MESSAGES_DELETE_REQUEST: 'CUSTOM_MESSAGES_DELETE##REQUEST',
    CUSTOM_MESSAGES_DELETE_SUCCESS: 'CUSTOM_MESSAGES_DELETE##SUCCESS',
    CUSTOM_MESSAGES_DELETE_FAILURE: 'CUSTOM_MESSAGES_DELETE##FAILURE',
    CUSTOM_MESSAGES_FETCH_REQUEST: 'CUSTOM_MESSAGES_FETCH##REQUEST',
    CUSTOM_MESSAGES_FETCH_SUCCESS: 'CUSTOM_MESSAGES_FETCH##SUCCESS',
    CUSTOM_MESSAGES_FETCH_FAILURE: 'CUSTOM_MESSAGES_FETCH##FAILURE',
}

/**
 * Fetch all custom messages from a store
 * @param {String} store Store Code
 */
function fetchStoreMessages(store) {
    const endpoint = qs.stringifyUrl({
        url: `${HUBBLE_API_TOKEN}/stores_messages`,
        query: { store },
    })

    return createAction({
        endpoint,
        method: 'GET',
        types: [Types.CUSTOM_MESSAGES_FETCH_REQUEST, Types.CUSTOM_MESSAGES_FETCH_SUCCESS, Types.CUSTOM_MESSAGES_FETCH_FAILURE],
    })
}

/**
 * Create a new custom message
 * @param {String} store Store Code
 */
function createStoreMessage(store, message = '') {
    if (!message) {
        throw new Error('Undefined `message`')
    }

    if (!store) {
        throw new Error('Undefined `store`')
    }

    const body = JSON.stringify({
        store_code: store,
        message,
    })

    return async (dispatch) => {
        const action = await dispatch(
            createAction({
                body,
                endpoint: `${HUBBLE_API_TOKEN}/stores_messages`,
                method: 'POST',
                types: [Types.CUSTOM_MESSAGES_CREATE_REQUEST, Types.CUSTOM_MESSAGES_CREATE_SUCCESS, Types.CUSTOM_MESSAGES_CREATE_FAILURE],
            })
        )

        const { type } = action

        return [type === Types.CUSTOM_MESSAGES_CREATE_SUCCESS, action]
    }
}

/**
 * Delete a custom message
 * @param {Number} storeMessageId
 */
function deleteStoreMessage(storeMessageId) {
    if (!storeMessageId) {
        throw new Error('Undefined `storeMessageId`')
    }

    return async (dispatch) => {
        const result = await dispatch(
            createAction({
                endpoint: `${HUBBLE_API_TOKEN}/stores_messages/${storeMessageId}`,
                method: 'DELETE',
                types: [
                    Types.CUSTOM_MESSAGES_DELETE_REQUEST,
                    {
                        type: Types.CUSTOM_MESSAGES_DELETE_SUCCESS,
                        meta: { id: storeMessageId },
                    },
                    Types.CUSTOM_MESSAGES_DELETE_FAILURE,
                ],
            })
        )

        return [result.type === Types.CUSTOM_MESSAGES_DELETE_SUCCESS, result]
    }
}

/**
 * Delete a custom message and check it is not present in any device
 * in the selected store
 * @param {Number} storeMessageId
 * @param {String} [message]
 */
function deleteStoreMessageAndCheckDevices(storeMessageId, message = null) {
    if (!storeMessageId) {
        throw new Error('Undefined `storeMessageId`')
    }

    return async (dispatch, getState) => {
        // Delete the message from the db
        const responseDelete = await dispatch(deleteStoreMessage(storeMessageId))
        const [ok] = responseDelete

        if (!message || !ok) {
            return responseDelete
        }

        // Get selected store's devices
        const devices = getSelectedStoreDevices(getState())

        // Iterate every device in the store, deleting the message from the device that
        // has it included
        const responseDevices = await Promise.all(
            devices.map((device) => {
                const { id, layer_extra } = device

                // Get device's custom message
                const customMessage = getCustomMessagesFromLayerExtra(layer_extra)

                // If the devices hasn't got the message, skip
                if (customMessage !== message) {
                    return null;
                }

                // Update the messages in the layer extra
                const newLayerExtra = setCustomMessagesIntoLayerExtra(layer_extra)
                return dispatch(updateLayerExtra(newLayerExtra, id))
            })
        )

        return responseDevices
    }
}

export { Types, createStoreMessage, deleteStoreMessage, deleteStoreMessageAndCheckDevices, fetchStoreMessages }
